import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, timeout } from 'rxjs/operators';
import { BrandKitService, ColorData, PublicAsset, PublicKit } from './brand-kit.service';

/** Color asset for display in picker */
export interface ColorPickerColor {
	value: string; // hex color, e.g., "#FF5733"
	name: string; // display name
}

/** Internal state for a single package */
interface PackageColorState {
	selectedKitId: string | null;
	subject: BehaviorSubject<string | null>;
}

@Injectable({ providedIn: 'root' })
export class BrandKitColorSelectionService implements OnDestroy {
	private readonly STORAGE_KEY_PREFIX = 'colorPicker.brandKit';

	/** Keyed state map - avoids singleton vs per-package contradiction */
	private stateMap = new Map<string, PackageColorState>();
	private destroy$ = new Subject<void>();

	/** Cache for kit colors to avoid repeated fetches */
	private kitColorsCache = new Map<string, Observable<ColorPickerColor[]>>();

	constructor(private brandKitService: BrandKitService) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		// Clean up all subjects
		this.stateMap.forEach(state => state.subject.complete());
		this.stateMap.clear();
		this.kitColorsCache.clear();
	}

	/**
	 * Get observable for kit selection changes for a specific package.
	 * Automatically initializes from localStorage if first access.
	 */
	getSelectedKitId$(packageId: string): Observable<string | null> {
		return this.getOrCreateState(packageId).subject.asObservable();
	}

	/**
	 * Get current kit ID synchronously (for initialization/templates)
	 */
	getSelectedKitId(packageId: string): string | null {
		return this.getOrCreateState(packageId).selectedKitId;
	}

	/**
	 * Get colors for the selected kit as observable
	 */
	getSelectedKitColors$(packageId: string): Observable<ColorPickerColor[]> {
		return this.getSelectedKitId$(packageId).pipe(
			switchMap(kitId => {
				if (!kitId) return of([]);
				return this.fetchKitColors(kitId);
			}),
			catchError(err => {
				console.error('Failed to fetch kit colors:', err);
				return of([]);
			}),
			shareReplay(1)
		);
	}

	/**
	 * Get the selected kit details as observable
	 */
	getSelectedKit$(packageId: string): Observable<PublicKit | null> {
		return this.getSelectedKitId$(packageId).pipe(
			switchMap(kitId => {
				if (!kitId) return of(null);
				return this.brandKitService.findAllKits().pipe(
					map(kits => kits.find(k => k.id === kitId) || null),
					catchError(() => of(null))
				);
			}),
			shareReplay(1)
		);
	}

	/**
	 * Select a brand kit for a package. Persists to localStorage.
	 */
	selectKit(packageId: string, kitId: string | null): void {
		const state = this.getOrCreateState(packageId);
		state.selectedKitId = kitId;
		state.subject.next(kitId);
		this.persistToStorage(packageId, kitId);
	}

	/**
	 * Validate that a saved kit still exists and user has access.
	 * Call this to handle deleted/inaccessible kits gracefully.
	 */
	async validateSavedKit(packageId: string): Promise<boolean> {
		const kitId = this.getSelectedKitId(packageId);
		if (!kitId) return true; // No kit saved is valid

		try {
			const kits = await this.brandKitService
				.findAllKits()
				.pipe(timeout(3000), take(1))
				.toPromise();

			const kitExists = kits?.some(k => k.id === kitId) ?? false;

			if (!kitExists) {
				// Kit no longer exists - clear selection
				this.selectKit(packageId, null);
				return false;
			}
			return true;
		} catch {
			// Network error - keep existing selection, validate later
			return true;
		}
	}

	/**
	 * Clear the colors cache for a specific kit (useful after kit updates)
	 */
	clearKitCache(kitId: string): void {
		this.kitColorsCache.delete(kitId);
	}

	// ============ Private Methods ============

	private getOrCreateState(packageId: string): PackageColorState {
		if (!this.stateMap.has(packageId)) {
			const savedKitId = this.loadFromStorage(packageId);
			const subject = new BehaviorSubject<string | null>(savedKitId);
			this.stateMap.set(packageId, {
				selectedKitId: savedKitId,
				subject
			});
		}
		return this.stateMap.get(packageId)!;
	}

	private loadFromStorage(packageId: string): string | null {
		try {
			return localStorage.getItem(this.storageKey(packageId));
		} catch (err) {
			// Private browsing or localStorage disabled
			if (err instanceof DOMException && err.name === 'SecurityError') {
				console.warn('localStorage unavailable (private browsing?)');
			}
			return null;
		}
	}

	private persistToStorage(packageId: string, kitId: string | null): void {
		try {
			const key = this.storageKey(packageId);
			if (kitId) {
				localStorage.setItem(key, kitId);
			} else {
				localStorage.removeItem(key);
			}
		} catch (err) {
			if (err instanceof DOMException) {
				if (err.name === 'QuotaExceededError') {
					console.warn('localStorage quota exceeded');
				} else if (err.name === 'SecurityError') {
					console.warn('localStorage unavailable');
				}
			}
			// Graceful degradation - selection works but won't persist
		}
	}

	private storageKey(packageId: string): string {
		return `${this.STORAGE_KEY_PREFIX}.${packageId}`;
	}

	private fetchKitColors(kitId: string): Observable<ColorPickerColor[]> {
		// Check cache first
		if (this.kitColorsCache.has(kitId)) {
			return this.kitColorsCache.get(kitId)!;
		}

		const colors$ = this.brandKitService.findAllKits().pipe(
			map(kits => kits.find(k => k.id === kitId)),
			switchMap(kit => {
				if (!kit?.brand?.slug) return of([]);
				return this.brandKitService.findAssets(kit.brand.slug, kit.slug, { types: ['color'] });
			}),
			map(assets =>
				assets.map(asset => ({
					value: (asset.data as ColorData).value,
					name: asset.name
				}))
			),
			catchError(() => of([])),
			shareReplay(1)
		);

		this.kitColorsCache.set(kitId, colors$);
		return colors$;
	}
}
